/**
 * MINEDEX map symbols, matching DMIRS.
 *
 * Verified 2026-09-01 against the AUTHORITATIVE renderer, not the legend:
 *
 *   https://services.slip.wa.gov.au/public/rest/services/
 *     SLIP_Public_Services/Industry_and_Mining/MapServer/0?f=json
 *
 * That returns drawingInfo.renderer = uniqueValue, field1 "commodity", 11
 * classes, each an esriPMS carrying a base64 PNG. Those PNGs are committed at
 * src/marker-icons/minedex-slip/ together with the raw response, so the claims
 * below can be re-checked without a network call. Every fill colour here was
 * read out of those pixels.
 *
 * The renderer has NO defaultSymbol, so DMIRS draws nothing for a commodity
 * outside the 11 - and 3 of the 46,770 rows have a NULL commodity. Rather than
 * drop three real sites, wa_geodata.minedex_sites_display coalesces them to
 * OTHER (MINEDEX NON-MINERALS) server-side, so every client inherits one
 * decision. See infrastructure/sql/2026-09-01-minedex-display-view.sql.
 *
 * WHY THESE ARE REDRAWN AS VECTORS RATHER THAN SHIPPING THE DMIRS PNGs.
 * The source art is 7-9 px (the 20x20 legend swatches are the same glyph
 * re-centred, not a larger rendering). Nine of the eleven are a flat square,
 * triangle or circle in one measured RGB, so a vector reproduction is EXACT,
 * not an approximation - and stays crisp at any zoom, size and device pixel
 * ratio. Blowing up anti-aliased 8px art to a phone-legible ~14px is visibly
 * soft, and registering it at its native size renders about 4 CSS px on a 2x
 * screen, which is the "looks like dirt" problem.
 *
 * Prospectors read GeoVIEW and our map side by side, so a site has to look like
 * the same site in both. The app previously used a prettier palette of its own
 * (a #1a5fb4 blue for IRON where DMIRS uses pure #0000FF, and so on), which made
 * the two maps disagree on sight.
 *
 * DMIRS draws every symbol with a darker outline of its own fill - measured at
 * roughly 40% of the fill value on the squares - so `OUTLINE_RATIO` reproduces
 * that rather than using one grey for all of them.
 */

export const OUTLINE_RATIO = 0.4;

/** shape: 'square' | 'triangle' | 'circle' | 'headframe' | 'cross' */
export const DMIRS_MINEDEX_SYMBOLS = {
  'ALUMINA':                      { shape: 'square',   fill: '#BF6347' },
  'IRON':                         { shape: 'square',   fill: '#0000FF' },
  'PRECIOUS METAL':               { shape: 'square',   fill: '#FFFF00' },
  'SPECIALITY METAL':             { shape: 'square',   fill: '#00FF00' },
  'STEEL ALLOY METAL':            { shape: 'square',   fill: '#FEAC00' },
  'BASE METAL':                   { shape: 'triangle', fill: '#FF0000' },
  'CONSTRUCTION MATERIAL':        { shape: 'triangle', fill: '#00DAFE' },
  'ENERGY':                       { shape: 'triangle', fill: '#000000' },
  'INDUSTRIAL MINERAL':           { shape: 'circle',   fill: '#008100' },
  // NOT a star. An earlier reading of the legend had this as a white star with
  // a grey outline; the actual DMIRS PNG is pure #000000 at up to 82% alpha and
  // has no fill at all. Decoded pixel-by-pixel it is a thin line glyph - a
  // short vertical mast, a wide horizontal bar under it, and an X cross-brace
  // below that: a poppet head / headframe, the standard mine-shaft symbol.
  'PRECIOUS MINERAL':             { shape: 'headframe', fill: '#000000', outline: '#000000' },
  // A thin magenta cross with no fill.
  'OTHER (MINEDEX NON-MINERALS)': { shape: 'cross',    fill: '#FF00FF', outline: '#FF00FF' },
};

/** Map-image ids, keyed the same way, so the style layers can look them up. */
export const minedexIconId = (commodityId) =>
  'minedex-' + commodityId.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Style-layer id for a commodity.
 *
 * Derived from the same table as the icon so the two cannot drift, and
 * exported because gl-map-optimized needs these in interactiveLayerIds - a
 * layer missing from that list is invisible to clicks and to hover, however
 * well it renders.
 */
export const minedexLayerId = (commodityId) => `${minedexIconId(commodityId)}-layer`;

export const MINEDEX_LAYER_IDS = Object.keys(DMIRS_MINEDEX_SYMBOLS).map(minedexLayerId);

const darken = (hex, ratio = OUTLINE_RATIO) => {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v * ratio));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
};

/**
 * The PRECIOUS MINERAL headframe, traced from the DMIRS PNG.
 *
 * Source pixels (columns of the 20x20 legend swatch, glyph occupies 7-14):
 *   row  7      mast          cols 10-11
 *   row  8      mast          cols 10-11
 *   row  9      bar           cols  7-14   <- widest element
 *   row 10      brace, wide   cols  8-9, 12-13
 *   row 11-12   brace, narrow cols  9-12
 *   row 13      brace, wide   cols  8, 13
 * Wide-narrow-wide under the bar is two lines crossing: an X, not a taper.
 */
const headframePath = (ctx, size, pad) => {
  const w = size - pad * 2;
  const cx = size / 2;
  const top = pad + w * 0.06;
  const barY = pad + w * 0.34;
  const botY = size - pad - w * 0.06;
  const half = w * 0.5;
  const inner = w * 0.28;
  ctx.beginPath();
  ctx.moveTo(cx, top);          // mast
  ctx.lineTo(cx, barY);
  ctx.moveTo(cx - half, barY);  // bar
  ctx.lineTo(cx + half, barY);
  ctx.moveTo(cx - inner, barY); // X cross-brace
  ctx.lineTo(cx + inner, botY);
  ctx.moveTo(cx + inner, barY);
  ctx.lineTo(cx - inner, botY);
};

/**
 * Renders one symbol to an ImageData the MapLibre style can addImage().
 *
 * Drawn at `pixelRatio` and handed back with that ratio so the symbol stays
 * crisp on a HiDPI screen - a flat 11px byte array (what this replaced) is soft
 * on any modern display.
 */
export function createMinedexSymbol(spec, size = 14, pixelRatio = 2) {
  const px = Math.round(size * pixelRatio);
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  ctx.scale(pixelRatio, pixelRatio);

  const outline = spec.outline || darken(spec.fill);
  const lw = 1;
  const pad = lw;                 // keep the stroke inside the bitmap
  const s = size - pad * 2;
  ctx.lineWidth = lw;
  ctx.strokeStyle = outline;
  ctx.fillStyle = spec.fill;
  ctx.lineJoin = 'round';

  switch (spec.shape) {
    case 'triangle':
      ctx.beginPath();
      ctx.moveTo(size / 2, pad);
      ctx.lineTo(size - pad, size - pad);
      ctx.lineTo(pad, size - pad);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      break;
    case 'circle':
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, s / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      break;
    case 'headframe':
      // Strokes only - the DMIRS glyph has no filled area.
      ctx.lineWidth = 1.3;
      ctx.lineCap = 'round';
      headframePath(ctx, size, pad);
      ctx.stroke();
      break;
    case 'cross':
      // No fill - DMIRS draws this one as strokes only.
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(pad + 1, pad + 1);
      ctx.lineTo(size - pad - 1, size - pad - 1);
      ctx.moveTo(size - pad - 1, pad + 1);
      ctx.lineTo(pad + 1, size - pad - 1);
      ctx.stroke();
      break;
    case 'square':
    default:
      ctx.fillRect(pad, pad, s, s);
      ctx.strokeRect(pad + 0.5, pad + 0.5, s - 1, s - 1);
      break;
  }

  return { imageData: ctx.getImageData(0, 0, px, px), pixelRatio };
}

/**
 * Registers every MINEDEX symbol on a map. Call from the style's load handler.
 *
 * Registers all eleven: the previous code added three (and had ALUMINA red
 * rather than DMIRS brown), so the other eight commodity layers referenced
 * images that were never created and drew nothing at all.
 */
export function addMinedexSymbols(map, size = 14) {
  Object.entries(DMIRS_MINEDEX_SYMBOLS).forEach(([commodityId, spec]) => {
    const id = minedexIconId(commodityId);
    if (map.hasImage(id)) return;
    const { imageData, pixelRatio } = createMinedexSymbol(spec, size);
    map.addImage(id, imageData, { pixelRatio });
  });
}

/**
 * One MINEDEX symbol as a PNG data URL, for the sidebar legend.
 *
 * The legend used to be a parallel list of Font Awesome classes and hex colours
 * in MinedexTypeData.js, kept in step with the map by hand - and it had already
 * drifted: PRECIOUS MINERAL was a grey outline star there while the map drew a
 * white one, and both were wrong (DMIRS draws a black headframe). Rendering the
 * legend from the SAME function the map registers means the two cannot disagree
 * again, and it does not depend on which Font Awesome build is loaded.
 *
 * Cached because a legend re-renders on every filter change and this allocates
 * a canvas.
 */
const legendCache = new Map();
export function minedexSymbolDataUrl(commodityId, size = 14) {
  const key = `${commodityId}@${size}`;
  if (legendCache.has(key)) return legendCache.get(key);
  const spec = DMIRS_MINEDEX_SYMBOLS[commodityId];
  if (!spec) return undefined;
  const pixelRatio = 2;
  const px = Math.round(size * pixelRatio);
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  ctx.putImageData(createMinedexSymbol(spec, size, pixelRatio).imageData, 0, 0);
  const url = canvas.toDataURL();
  legendCache.set(key, url);
  return url;
}
